import { Check, ChevronDown, Clock3, LogIn, ShieldCheck, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { wave, waveQueries } from '@round/api';
import AudioLinks from '@/creator/components/AudioLinks';
import CampaignArtwork from '@/creator/components/CampaignArtwork';
import PlatformIcon from '@/creator/components/PlatformIcon';
import WaveLogo from '@/creator/components/WaveLogo';
import { BriefText } from '@/ui/BriefSection/BriefSection';
import PublicLoginTakeover from '@/ui/PublicLoginTakeover/PublicLoginTakeover';
import { useWaveAuth } from '@/auth/WaveAuthContext';
import { getTimeLeft, PLATFORM_ICON, PLATFORM_LABEL } from '@/utility/campaignDisplay';
import { showNotification } from '@/utility/showNotification';
import '@/creator-app.css';

const SUPPORT_EMAIL = 'hello@wave.me';

const formatClosesAt = (deadline: string | null): string => {
    if (!deadline) {
        return '';
    }
    return new Date(deadline).toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

const CampaignHero = ({ campaign, isClosed }: { campaign: wave.PublicCampaign; isClosed: boolean }) => {
    const closesAt = formatClosesAt(campaign.deadline);
    // A closed campaign's remaining deadline is still true but reads as an invitation,
    // so the countdown gives way to the state that actually governs the page.
    const timelineLabel = isClosed
        ? 'Submissions closed'
        : `${getTimeLeft(campaign.deadline)}${closesAt ? ` · closes ${closesAt}` : ''}`;
    return (
        <section className="public-campaign-hero">
            <CampaignArtwork src={campaign.artwork_url} alt={`${campaign.release_name} artwork`} size="detail" />
            <div className="public-campaign-hero__copy">
                <h1>{campaign.release_name}</h1>
                <p>{`A creator campaign from ${campaign.artist_name}.`}</p>
                <span className="deadline">
                    <Clock3 size={14} /> <span>{timelineLabel}</span>
                </span>
            </div>
        </section>
    );
};

const PageHeader = ({ isAuthenticated, onLogin }: { isAuthenticated: boolean; onLogin: () => void }) => (
    <header className="public-campaign-header">
        <WaveLogo compact />
        {isAuthenticated ? (
            <Link className="public-login-button" to="/">
                Your Wave
            </Link>
        ) : (
            <button className="public-login-button" type="button" onClick={onLogin}>
                <LogIn size={14} /> <span>Log in</span>
            </button>
        )}
    </header>
);

const postingRequirement = (maxPostsPerCreator: number | null): string => {
    if (maxPostsPerCreator === null) {
        return 'Unlimited posts per creator';
    }
    return `${maxPostsPerCreator} ${maxPostsPerCreator === 1 ? 'post' : 'posts'} per creator`;
};

type CampaignContentProps = {
    campaign: wave.PublicCampaign;
    isAuthenticated: boolean;
    isRegisteringInterest: boolean;
    onLogin: () => void;
    onRegisterInterest: () => void;
};

const CampaignContent = ({
    campaign,
    isAuthenticated,
    isRegisteringInterest,
    onLogin,
    onRegisterInterest,
}: CampaignContentProps) => {
    const [briefExpanded, setBriefExpanded] = useState(false);
    const closesAt = formatClosesAt(campaign.deadline);
    const isInterested = campaign.viewer?.has_registered_interest ?? false;
    // A campaign that cannot take work from anyone must not present as recruiting,
    // whichever way it got there. Mirrors the invite page's terminal states.
    const isFull = campaign.submission_availability === 'full';
    const isClosed = campaign.submission_availability !== 'open';

    return (
        <main className="public-campaign-main">
            <CampaignHero campaign={campaign} isClosed={isClosed} />
            <div className="public-campaign-layout">
                <div className="public-campaign-content">
                    <section className="public-campaign-facts" aria-label="Campaign details">
                        <div>
                            <span>Platforms</span>
                            <strong
                                className="public-platforms"
                                aria-label={campaign.platforms
                                    .map((platform) => PLATFORM_LABEL[platform])
                                    .join(' and ')}
                            >
                                {campaign.platforms.map((platform) => (
                                    <span
                                        className={`platform-logo platform-logo--${PLATFORM_ICON[
                                            platform
                                        ].toLowerCase()}`}
                                        key={platform}
                                    >
                                        <PlatformIcon platform={PLATFORM_ICON[platform]} size={14} />
                                    </span>
                                ))}
                            </strong>
                        </div>
                        {isClosed ? (
                            <div>
                                <span>Submissions</span>
                                <strong>Closed</strong>
                            </div>
                        ) : (
                            <>
                                <div>
                                    <span>Posting requirement</span>
                                    <strong>{postingRequirement(campaign.max_posts_per_creator)}</strong>
                                </div>
                                <div>
                                    <span>Campaign closes</span>
                                    <strong>{closesAt || 'When the campaign fills'}</strong>
                                </div>
                            </>
                        )}
                    </section>

                    {campaign.audios.length > 0 ? (
                        <section className="public-section">
                            <div className="public-section__heading">
                                <h2>{campaign.audios.length === 1 ? 'The audio' : 'The audios'}</h2>
                            </div>
                            <AudioLinks audios={campaign.audios} />
                        </section>
                    ) : null}

                    <section className={`public-section public-brief ${briefExpanded ? 'is-expanded' : ''}`}>
                        <div className="public-section__heading">
                            <h2>The brief</h2>
                            <button
                                type="button"
                                onClick={() => setBriefExpanded((current) => !current)}
                                aria-expanded={briefExpanded}
                            >
                                <span>{briefExpanded ? 'Collapse brief' : 'Expand brief'}</span>{' '}
                                <ChevronDown size={14} />
                            </button>
                        </div>
                        <div className="public-brief__copy">
                            <BriefText brief={campaign.brief} />
                        </div>
                    </section>
                </div>

                <aside className="public-action-card">
                    {isClosed ? (
                        <>
                            <span className="public-action-card__icon">
                                {isFull ? <Check size={18} /> : <Clock3 size={18} />}
                            </span>
                            <h2>{isFull ? 'Campaign closed' : 'Submissions are closed'}</h2>
                            <p>
                                {isFull
                                    ? 'This campaign is full and is no longer accepting posts.'
                                    : 'The window for this campaign has ended.'}{' '}
                                If you already made your post, get in touch at {SUPPORT_EMAIL}.
                            </p>
                        </>
                    ) : isAuthenticated ? (
                        isInterested ? (
                            <>
                                <span className="public-action-card__icon">
                                    <Check size={18} />
                                </span>
                                <h2>Interest registered</h2>
                                <p>Thanks — the Wave team knows you’d like to take part.</p>
                            </>
                        ) : (
                            <>
                                <span className="public-action-card__icon">
                                    <UserPlus size={18} />
                                </span>
                                <h2>Want to take part?</h2>
                                <p>Register your interest and the Wave team can follow up if you’re a match.</p>
                                <button
                                    className="button button--primary"
                                    type="button"
                                    disabled={isRegisteringInterest || !campaign.is_open}
                                    onClick={onRegisterInterest}
                                >
                                    {isRegisteringInterest ? 'Registering…' : 'Register interest'}
                                </button>
                            </>
                        )
                    ) : (
                        <>
                            <span className="public-action-card__icon">
                                <ShieldCheck size={18} />
                            </span>
                            <h2>Apply or log in</h2>
                            <p>Enter your email to continue. If you have a Wave account, we’ll send a login code.</p>
                            <button className="button button--primary" type="button" onClick={onLogin}>
                                Continue with email
                            </button>
                            <small>Already invited? Your personal invite link opens without logging in.</small>
                        </>
                    )}
                </aside>
            </div>
        </main>
    );
};

const PublicCampaignPage = () => {
    const { token = '' } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { isAuthenticated } = useWaveAuth();
    const [showLogin, setShowLogin] = useState(false);
    const [isRegisteringInterest, setIsRegisteringInterest] = useState(false);
    const query = useQuery(waveQueries.publicCampaign.options.detail(token, isAuthenticated));

    const handleAuthenticated = async (): Promise<void> => {
        try {
            const campaign = await queryClient.fetchQuery(waveQueries.publicCampaign.options.detail(token, true));
            if (campaign.viewer?.invite_uuid) {
                navigate(`/campaigns/${campaign.viewer.invite_uuid}`, { replace: true });
                return;
            }
        } catch {
            // Authentication has already succeeded. Return to the campaign and
            // let its auth-scoped query own any follow-up load error.
        }
        setShowLogin(false);
    };

    const captureEmail = (email: string): void => {
        // Demand capture is deliberately best-effort and neutral. LoginByCode owns
        // the identical immediate response for existing and unknown addresses.
        void wave.capturePublicCampaignEmail(token, { email }).catch(() => undefined);
    };

    const registerInterest = async (): Promise<void> => {
        setIsRegisteringInterest(true);
        try {
            const result = await wave.registerPublicCampaignInterest(token);
            if (result.status === 200) {
                await query.refetch();
                showNotification('Your interest has been registered.', 'success');
                return;
            }
            showNotification('This campaign is no longer available.', 'error');
        } catch {
            showNotification('We couldn’t register your interest. Please try again.', 'error');
        } finally {
            setIsRegisteringInterest(false);
        }
    };

    if (showLogin) {
        return (
            <PublicLoginTakeover
                backLabel="Back to campaign"
                onBack={() => setShowLogin(false)}
                onAuthenticated={handleAuthenticated}
                onEmailRequested={captureEmail}
            />
        );
    }

    if (query.data?.viewer?.invite_uuid) {
        return <Navigate to={`/campaigns/${query.data.viewer.invite_uuid}`} replace />;
    }

    if (query.isLoading) {
        return (
            <div className="public-campaign-page public-campaign-page--public">
                <PageHeader isAuthenticated={isAuthenticated} onLogin={() => setShowLogin(true)} />
                <main className="public-campaign-main">
                    <p className="page">Loading campaign…</p>
                </main>
            </div>
        );
    }

    if (query.isError || !query.data) {
        return (
            <div className="public-campaign-page public-campaign-page--public">
                <PageHeader isAuthenticated={isAuthenticated} onLogin={() => setShowLogin(true)} />
                <main className="public-campaign-main">
                    <section className="public-section">
                        <div className="public-section__heading">
                            <h2>This link isn’t working</h2>
                        </div>
                        <div className="public-brief__copy">
                            <p>
                                The campaign link may be invalid or expired. Check the link you were sent, or contact
                                hello@wave.me.
                            </p>
                        </div>
                    </section>
                </main>
            </div>
        );
    }

    return (
        <div className="public-campaign-page public-campaign-page--public">
            <PageHeader isAuthenticated={isAuthenticated} onLogin={() => setShowLogin(true)} />
            <CampaignContent
                campaign={query.data}
                isAuthenticated={isAuthenticated}
                isRegisteringInterest={isRegisteringInterest}
                onLogin={() => setShowLogin(true)}
                onRegisterInterest={() => void registerInterest()}
            />
        </div>
    );
};

export default PublicCampaignPage;
